import { useParams, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLanguage } from "@/i18n";
import { useSEO } from "@/hooks/useSEO";

const LEGAL = {
  privacy: {
    title: { it: "Privacy Policy", en: "Privacy Policy" },
    sections: [
      {
        h: { it: "Titolare del trattamento", en: "Data Controller" },
        body: {
          it: "Flegrea Experience — [Denominazione legale da completare], con sede in [Indirizzo sede], P.IVA [da completare], email info@flegreaexperience.com, PEC [da completare se applicabile]. Il Titolare tratta i dati personali degli utenti nel rispetto del Regolamento (UE) 2016/679 (GDPR).",
          en: "Flegrea Experience — [Legal name to be completed], registered office [address], VAT [to be completed], email info@flegreaexperience.com, certified email (PEC) [if applicable]. The Controller processes personal data in compliance with Regulation (EU) 2016/679 (GDPR).",
        },
      },
      {
        h: { it: "Dati raccolti", en: "Data collected" },
        body: {
          it: "Raccogliamo i dati forniti volontariamente tramite form e configuratore (nome, email, telefono, preferenze di viaggio), i dati di account Google se effettui l'accesso (nome, email, foto profilo) e dati tecnici di navigazione. I dati sono utilizzati esclusivamente per rispondere alle richieste, gestire prenotazioni e migliorare il servizio.",
          en: "We collect data voluntarily provided via forms and the configurator (name, email, phone, travel preferences), Google account data if you sign in (name, email, profile picture) and technical browsing data. Data is used exclusively to respond to requests, manage bookings and improve the service.",
        },
      },
      {
        h: { it: "Base giuridica e conservazione", en: "Legal basis and retention" },
        body: {
          it: "Il trattamento si basa sull'esecuzione di misure precontrattuali richieste dall'utente, sul consenso ove richiesto e sull'interesse legittimo del Titolare. I dati sono conservati per il tempo necessario alle finalità indicate e comunque secondo i termini di legge.",
          en: "Processing is based on pre-contractual measures requested by the user, on consent where required, and on the Controller's legitimate interest. Data is retained for the time necessary for the stated purposes and in any case within legal terms.",
        },
      },
      {
        h: { it: "Diritti dell'interessato", en: "Your rights" },
        body: {
          it: "Puoi richiedere in qualsiasi momento accesso, rettifica, cancellazione, limitazione e portabilità dei tuoi dati, nonché opporti al trattamento, scrivendo a info@flegreaexperience.com. Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali.",
          en: "You may request access, rectification, deletion, restriction and portability of your data at any time, and object to processing, by writing to info@flegreaexperience.com. You also have the right to lodge a complaint with the Italian Data Protection Authority.",
        },
      },
    ],
  },
  cookie: {
    title: { it: "Cookie Policy", en: "Cookie Policy" },
    sections: [
      {
        h: { it: "Cosa sono i cookie", en: "What cookies are" },
        body: {
          it: "I cookie sono piccoli file di testo che il sito salva sul tuo dispositivo. Utilizziamo cookie tecnici necessari al funzionamento (sessione, preferenze lingua e consenso) e, solo previo consenso, cookie analitici per comprendere l'utilizzo del sito.",
          en: "Cookies are small text files the site saves on your device. We use technical cookies required for operation (session, language and consent preferences) and, only with your consent, analytics cookies to understand site usage.",
        },
      },
      {
        h: { it: "Gestione del consenso", en: "Consent management" },
        body: {
          it: "Al primo accesso puoi accettare tutti i cookie, rifiutare quelli non necessari o personalizzare le preferenze. Puoi modificare la tua scelta in qualsiasi momento dal link 'Gestisci preferenze cookie' nel footer.",
          en: "On your first visit you can accept all cookies, reject non-essential ones or customise your preferences. You can change your choice at any time via the 'Manage cookie preferences' link in the footer.",
        },
      },
      {
        h: { it: "Cookie di terze parti", en: "Third-party cookies" },
        body: {
          it: "L'accesso con Google è gestito tramite il provider di autenticazione; eventuali cookie di terze parti sono soggetti alle rispettive informative. Nessuno strumento non necessario viene caricato prima del consenso.",
          en: "Google sign-in is managed through the authentication provider; any third-party cookies are subject to their respective policies. No non-essential tools are loaded before consent.",
        },
      },
    ],
  },
  terms: {
    title: { it: "Termini e Condizioni", en: "Terms & Conditions" },
    sections: [
      {
        h: { it: "Oggetto del servizio", en: "Service scope" },
        body: {
          it: "Flegrea Experience propone e organizza esperienze turistiche in Campania (visite guidate, escursioni in barca e in canoa, trekking, esperienze enogastronomiche, soggiorni selezionati). I contenuti del sito hanno scopo informativo; prezzi e disponibilità sono indicativi fino a conferma.",
          en: "Flegrea Experience proposes and organises tourism experiences in Campania (guided tours, boat and canoe excursions, trekking, food & wine experiences, selected stays). Site content is informative; prices and availability are indicative until confirmed.",
        },
      },
      {
        h: { it: "Prenotazioni e pagamenti", en: "Bookings and payments" },
        body: {
          it: "La richiesta di prenotazione tramite sito o WhatsApp non costituisce conferma: l'esperienza è confermata solo dopo nostra comunicazione scritta con dettagli, prezzo finale e modalità di pagamento concordate. Nessun pagamento online è attualmente richiesto sul sito.",
          en: "A booking request via the site or WhatsApp does not constitute confirmation: the experience is confirmed only after our written communication with details, final price and agreed payment methods. No online payment is currently required on the site.",
        },
      },
      {
        h: { it: "Responsabilità e sicurezza", en: "Liability and safety" },
        body: {
          it: "Le attività all'aperto sono soggette a condizioni meteo, marine e normative locali. Flegrea Experience può modificare o annullare un'esperienza per motivi di sicurezza, offrendo alternativa o rimborso di quanto versato. La partecipazione richiede il rispetto delle indicazioni di guide e operatori.",
          en: "Outdoor activities are subject to weather, sea conditions and local regulations. Flegrea Experience may modify or cancel an experience for safety reasons, offering an alternative or a refund of any amounts paid. Participation requires following the instructions of guides and operators.",
        },
      },
      {
        h: { it: "Legge applicabile", en: "Governing law" },
        body: {
          it: "I presenti termini sono regolati dalla legge italiana. Per qualsiasi controversia è competente il foro di [da completare in sede di verifica legale].",
          en: "These terms are governed by Italian law. Any dispute falls under the jurisdiction of [to be completed upon legal review].",
        },
      },
    ],
  },
  "booking-info": {
    title: { it: "Informazioni sulle Prenotazioni", en: "Booking Information" },
    sections: [
      {
        h: { it: "Come funziona", en: "How it works" },
        body: {
          it: "Scegli l'esperienza, indica data, partecipanti ed eventuali servizi aggiuntivi, invia la richiesta. Ti ricontattiamo per confermare disponibilità, prezzo finale e punto d'incontro. Puoi sempre scriverci su WhatsApp per qualsiasi dubbio prima di prenotare.",
          en: "Choose the experience, indicate date, participants and any add-ons, send the request. We will get back to you to confirm availability, final price and meeting point. You can always message us on WhatsApp with any questions before booking.",
        },
      },
      {
        h: { it: "Stati della richiesta", en: "Request statuses" },
        body: {
          it: "Richiesta inviata: abbiamo ricevuto la tua richiesta. In attesa di conferma: stiamo verificando disponibilità. Confermata: l'esperienza è riservata per te. Completata: esperienza vissuta. Annullata: richiesta annullata.",
          en: "Request sent: we have received your request. Pending confirmation: we are checking availability. Confirmed: the experience is reserved for you. Completed: experience enjoyed. Cancelled: request cancelled.",
        },
      },
      {
        h: { it: "Esperienze private e personalizzate", en: "Private and custom experiences" },
        body: {
          it: "Per esperienze private, gruppi oltre 10 persone o itinerari su misura utilizza il configuratore 'Crea la tua esperienza' o contattaci: costruiremo una proposta dedicata con preventivo.",
          en: "For private experiences, groups over 10 people or tailor-made itineraries use the 'Create Your Experience' configurator or contact us: we will build a dedicated proposal with a quote.",
        },
      },
    ],
  },
  cancellation: {
    title: { it: "Politica di Cancellazione", en: "Cancellation Policy" },
    sections: [
      {
        h: { it: "Cancellazione da parte del cliente", en: "Customer cancellation" },
        body: {
          it: "Salvo diverse condizioni concordate per esperienze private: cancellazione gratuita fino a 72 ore prima dell'esperienza; tra 72 e 24 ore può essere trattenuto un acconto concordato; oltre le 24 ore o mancata presentazione, l'importo concordato può essere dovuto integralmente. [Da verificare in sede legale.]",
          en: "Unless different conditions are agreed for private experiences: free cancellation up to 72 hours before the experience; between 72 and 24 hours an agreed deposit may be retained; within 24 hours or no-show, the agreed amount may be due in full. [To be verified upon legal review.]",
        },
      },
      {
        h: { it: "Meteo e sicurezza", en: "Weather and safety" },
        body: {
          it: "Se le condizioni meteo o marine non consentono lo svolgimento in sicurezza, l'esperienza viene riprogrammata o rimborsata integralmente, a tua scelta. La valutazione spetta agli operatori e alle guide.",
          en: "If weather or sea conditions prevent safe operation, the experience is rescheduled or fully refunded, at your choice. The assessment is made by operators and guides.",
        },
      },
      {
        h: { it: "Modifiche", en: "Changes" },
        body: {
          it: "Puoi chiedere la modifica di data o partecipanti contattandoci: faremo il possibile per accoglierla in base alla disponibilità.",
          en: "You can request changes to date or participants by contacting us: we will do our best to accommodate based on availability.",
        },
      },
    ],
  },
};

export default function Legal() {
  const { slug } = useParams();
  const { lang, t } = useLanguage();
  const doc = LEGAL[slug];
  useSEO(doc ? `${pickSafe(doc.title)} — Flegrea Experience` : "Flegrea Experience", t("tagline"));

  function pickSafe(o) {
    return o[lang] || o.en;
  }

  if (!doc) {
    return (
      <div className="py-40 text-center" data-testid="legal-not-found">
        <p className="font-serif text-3xl text-ink">404</p>
        <Link to="/" className="mt-6 inline-block text-sm text-medblue underline">Flegrea Experience</Link>
      </div>
    );
  }

  return (
    <div className="py-16 lg:py-24" data-testid={`legal-page-${slug}`}>
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ink/40 hover:text-ink transition-colors duration-300 mb-10">
          <ArrowLeft size={13} strokeWidth={1.5} /> Flegrea Experience
        </Link>
        <h1 className="font-serif text-4xl md:text-5xl text-ink tracking-tight">{doc.title[lang] || doc.title.en}</h1>

        <div className="mt-6 border border-terracotta/60 bg-terracotta/10 p-5" data-testid="legal-placeholder-note">
          <p className="text-xs text-ink/70 leading-relaxed">
            {lang === "it"
              ? "Documento in bozza professionale: i campi tra parentesi quadre (denominazione, sede, P.IVA, PEC, dati del titolare) devono essere completati e il testo verificato da un professionista prima della pubblicazione."
              : "Professional draft document: fields in square brackets (legal name, address, VAT, certified email, owner details) must be completed and the text reviewed by a professional before publication."}
          </p>
        </div>

        <div className="mt-12 space-y-12">
          {doc.sections.map((s, i) => (
            <section key={i}>
              <h2 className="font-serif text-2xl text-ink mb-3">{s.h[lang] || s.h.en}</h2>
              <p className="text-sm md:text-base text-ink/70 leading-relaxed">{s.body[lang] || s.body.en}</p>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
